import React, { useState, useCallback } from "react";
import {
  Card,
  CardHeader,
  CardFooter,
  CardBody,
  Form,
  Input,
  FormGroup,
  Label,
  UncontrolledAlert,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { apiService } from "../../services";
import SubmitButton from "../Others/SubmitButton";

const initialState = {
  currentPassword: "",
  newPassword: "",
  newPasswordConfirm: "",
  isPasswordVisible: false,
};

export const PasswordForm = ({ children, isFirstTime = false }) => {
  const dispatch = useDispatch();
  const userId = useSelector((state) => state.user.data.id);
  const error = useSelector((state) => state.user.error);
  const isFetching = useSelector((state) => state.user.isFetching);

  const [formData, setFormData] = useState(initialState);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [isNewPasswordConfirmed, setIsNewPasswordConfirmed] = useState(false);

  const handleChange = useCallback((ev) => {
    setFormData((prevState) => ({
      ...prevState,
      [ev.target.id]: ev.target.value,
    }));
  }, []);

  const changePassword = useCallback(async () => {
    try {
      await dispatch(
        apiService.changeUserPassword(
          userId,
          formData.currentPassword,
          formData.newPassword,
          isFirstTime
        )
      );

      // if success, reset form fields
      if (!error) {
        setFormData(initialState);
        setIsFormSubmitted(false);
        setIsNewPasswordConfirmed(false);
      }
    } catch (e) {
      console.log(e);
    }
  }, [dispatch, userId, formData, isFirstTime, error]);

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      const { newPassword, newPasswordConfirm } = formData;

      if (newPassword !== "" && newPassword === newPasswordConfirm) {
        setIsFormSubmitted(true);
        setIsNewPasswordConfirmed(true);
        changePassword();
      } else {
        setIsFormSubmitted(true);
        setIsNewPasswordConfirmed(false);
      }
    },
    [formData, changePassword]
  );

  const togglePassword = useCallback(() => {
    setFormData((prevState) => ({
      ...prevState,
      isPasswordVisible: !prevState.isPasswordVisible,
    }));
  }, []);

  const passwordInputType = formData.isPasswordVisible ? "text" : "password";

  return (
    <div className="form-wrapper">
      <Form onSubmit={handleSubmit}>
        <Card className="password-form">
          <CardHeader>
            <FormattedMessage id="passwordForm.header" />
          </CardHeader>
          <CardBody>
            {children}
            <FormGroup>
              <Label for="currentPassword">
                <FormattedMessage id="passwordForm.currentPassword" />
              </Label>
              <Input
                id="currentPassword"
                type={passwordInputType}
                value={formData.currentPassword}
                onChange={handleChange}
              />
            </FormGroup>
            <FormGroup>
              <Label for="newPassword">
                <FormattedMessage id="passwordForm.newPassword" />
              </Label>
              <Input
                id="newPassword"
                type={passwordInputType}
                value={formData.newPassword}
                onChange={handleChange}
              />
            </FormGroup>

            <FormGroup>
              <Label for="newPasswordConfirm">
                <FormattedMessage id="passwordForm.newPasswordConfirm" />
              </Label>
              <Input
                id="newPasswordConfirm"
                type={passwordInputType}
                value={formData.newPasswordConfirm}
                onChange={handleChange}
              />
            </FormGroup>
            <FormGroup check className="my-3">
              <Label check>
                <Input
                  type="checkbox"
                  id="isPasswordVisible"
                  onChange={togglePassword}
                  checked={formData.isPasswordVisible}
                />{" "}
                <FormattedMessage id="passwordForm.showPassword" />
              </Label>
            </FormGroup>
            {!isFetching && (
              <>
                {isFormSubmitted && isNewPasswordConfirmed && error && (
                  <UncontrolledAlert color="danger">
                    <FormattedMessage id="passwordForm.changeError" />
                  </UncontrolledAlert>
                )}
                {isFormSubmitted && !isNewPasswordConfirmed && (
                  <UncontrolledAlert color="danger">
                    <FormattedMessage id="passwordForm.confirmPasswordError" />
                  </UncontrolledAlert>
                )}
                {isFormSubmitted && isNewPasswordConfirmed && !error && (
                  <UncontrolledAlert color="success">
                    <FormattedMessage id="passwordForm.success" />
                  </UncontrolledAlert>
                )}
              </>
            )}
          </CardBody>
          <CardFooter className="text-right">
            <SubmitButton isLoading={isFetching}>
              <FormattedMessage id="passwordForm.submitButton" />
            </SubmitButton>
          </CardFooter>
        </Card>
      </Form>
    </div>
  );
};

PasswordForm.propTypes = {
  children: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  isFirstTime: PropTypes.bool,
};

export default PasswordForm;
